import React, { useCallback, useState } from "react";
import { Send, Share2, X } from "lucide-react";
import ShareNoteModal from "./ShareNoteModal";

/**
 * NoteComposer — textarea autogrow + selettore condivisione + bottone save.
 *
 * Props:
 *   meeting: oggetto riunione (per client_id → ShareNoteModal)
 *   draft: {body: string}
 *   setDraft(next): aggiorna draft (per autosave localStorage)
 *   initialShared: {share_mode, staff_user_ids, client_user_ids}
 *   editing: oggetto nota in edit o null
 *   onSubmit(body, shared): async POST/PATCH
 *   onCancelEdit()
 *
 * Shortcut tastiera: Ctrl+Enter / Cmd+Enter → submit.
 */
const MODE_LABELS = {
    private: "Solo io",
    specific_staff: "Staff specifici",
    all_participants: "Tutti i partecipanti",
    specific_clients: "Cliente",
    mixed: "Staff + Cliente",
};

export default function NoteComposer({
    meeting, draft, setDraft,
    initialShared, editing,
    onSubmit, onCancelEdit,
}) {
    const [shared, setShared] = useState(initialShared || {
        share_mode: "private", staff_user_ids: [], client_user_ids: [],
    });
    const [showShare, setShowShare] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = useCallback(async () => {
        if (!draft.body || !draft.body.trim() || submitting) return;
        setSubmitting(true);
        try {
            await onSubmit(draft.body.trim(), shared);
            setDraft({ body: "" });
            setShared({ share_mode: "private", staff_user_ids: [], client_user_ids: [] });
        } finally {
            setSubmitting(false);
        }
    }, [draft, shared, onSubmit, submitting, setDraft]);

    const onKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div
            data-testid="note-composer"
            style={{
                background: "#FFFFFF", border: "1px solid #DADEDA",
                borderRadius: 4, padding: 12, display: "flex", flexDirection: "column", gap: 10,
            }}
        >
            <textarea
                className="lt-textarea"
                rows={3}
                placeholder="Scrivi una nota di riunione… (Ctrl+Enter per salvare)"
                value={draft.body || ""}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                onKeyDown={onKeyDown}
                maxLength={8000}
                style={{
                    width: "100%", border: "none", outline: "none",
                    resize: "vertical", minHeight: 60, maxHeight: 220,
                    fontSize: 14, fontFamily: "inherit",
                }}
                data-testid="note-composer-textarea"
            />
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <button
                    type="button"
                    onClick={() => setShowShare(true)}
                    data-testid="note-composer-share-btn"
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "6px 12px", background: "#EFEFEC", color: "#181B20",
                        border: "1px solid #DADEDA", borderRadius: 3, cursor: "pointer",
                        fontFamily: '"IBM Plex Mono", monospace', fontSize: 11,
                        letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600,
                    }}
                >
                    <Share2 size={12} /> {MODE_LABELS[shared.share_mode] || "Solo io"}
                </button>
                <div style={{ flex: 1 }} />
                {editing && (
                    <button
                        type="button"
                        className="lt-btn-secondary"
                        onClick={onCancelEdit}
                        data-testid="note-composer-cancel-btn"
                    >
                        <X size={12} /> Annulla
                    </button>
                )}
                <button
                    type="button"
                    className="lt-btn-primary"
                    onClick={handleSubmit}
                    disabled={!draft.body || !draft.body.trim() || submitting}
                    data-testid="note-composer-submit-btn"
                >
                    <Send size={12} /> {editing ? "Aggiorna" : "Salva nota"}
                </button>
            </div>
            {showShare && (
                <ShareNoteModal
                    meeting={meeting}
                    value={shared}
                    onChange={setShared}
                    onClose={() => setShowShare(false)}
                />
            )}
        </div>
    );
}
